// API client for Lantern server
//
// baseURL resolution:
//   1. REACT_APP_API_URL — explicit override (any environment).
//   2. Production build (NODE_ENV=production) — '/api' so requests go to the
//      same origin that served the React app. On Railway the React client and
//      Express API are served from the same domain, so no cross-origin needed.
//   3. Development (npm start) — 'http://localhost:3001/api', because the dev
//      server runs on :3000 and the API is on :3001.
//
// Note: empty string would NOT work as a production fallback — all server
// routes live under /api/*, so the prefix has to be present.

import axios from 'axios';

const defaultBaseUrl = process.env.NODE_ENV === 'production'
  ? '/api'
  : 'http://localhost:3001/api';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || defaultBaseUrl,
  withCredentials: true,
});

// Attach the child or parent access token from localStorage on every request.
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('accessToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ─── Response interceptor: silent refresh on 401 ─────────────────────────────
//
// When a 401 comes back for a parent request, attempt POST /auth/refresh
// (which uses the HttpOnly refresh cookie set at login) and retry the
// original request transparently. If the refresh fails — refresh cookie
// missing, expired, revoked, or this is a child session (children have no
// refresh tokens) — clear local auth and bounce to the appropriate login.
//
// The same in-flight refresh promise is shared across concurrent failing
// requests, so a burst of expired-token responses results in exactly one
// /auth/refresh round-trip rather than N parallel ones.

// Endpoints whose 401 responses are NOT "expired session" — refresh would be
// pointless and would obscure the real reason (bad password, bad PIN, etc).
const NO_REFRESH_ENDPOINTS = [
  '/auth/refresh',
  '/auth/login',
  '/auth/2fa/login',
  '/auth/child-login',
  '/auth/logout',
];

function isAuthEndpoint(url) {
  if (!url) return false;
  return NO_REFRESH_ENDPOINTS.some((p) => url.includes(p));
}

let refreshPromise = null;

function redirectToLogin() {
  if (typeof window === 'undefined') return;
  // Pick parent vs child login based on which area of the app the user is in.
  // Child routes live under /child/*; everything else is parent-side.
  const path = window.location.pathname;
  const target = path.startsWith('/child') ? '/login' : '/parent/login';
  if (path !== target) {
    window.location.href = target;
  }
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error?.config;
    const status = error?.response?.status;

    // Bail out for any case where attempting refresh is wrong or would loop:
    //   - non-401 errors
    //   - already retried this request once
    //   - the failing request itself is a login/refresh/logout
    //   - no config (network error before request was sent)
    if (
      status !== 401
      || !originalRequest
      || originalRequest._retry
      || isAuthEndpoint(originalRequest.url)
    ) {
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    // Coalesce concurrent refreshes — only one POST /auth/refresh in flight.
    // Subsequent 401s reuse the same promise and retry once it settles.
    if (!refreshPromise) {
      refreshPromise = api
        .post('/auth/refresh')
        .then((res) => {
          const newToken = res?.data?.accessToken;
          if (newToken) localStorage.setItem('accessToken', newToken);
          return newToken;
        })
        .catch((err) => {
          // Refresh failed — wipe local state so the next request doesn't
          // attach a known-stale token. The redirect happens in the catch
          // below so each failed request gets a chance to surface the error.
          localStorage.removeItem('accessToken');
          throw err;
        })
        .finally(() => {
          refreshPromise = null;
        });
    }

    try {
      const newToken = await refreshPromise;
      // Apply the fresh token explicitly. The request interceptor would do
      // this on its own, but axios may have already snapshot the headers
      // before the refresh completed.
      if (newToken) {
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
      }
      return api(originalRequest);
    } catch {
      redirectToLogin();
      return Promise.reject(error);
    }
  },
);

// ─── Child auth API ───────────────────────────────────────────────────────────

export const childLogin = (username, pin, familyId) =>
  api.post('/auth/child-login', { username, pin, familyId });

// ─── Parent auth API ──────────────────────────────────────────────────────────

export const parentLogin = (email, password) =>
  api.post('/auth/login', { email, password });

export const parentTwoFactorLogin = (tempToken, code) =>
  api.post('/auth/2fa/login', { tempToken, token: code });

export const parentRegister = (email, password, confirmPassword, consents) =>
  api.post('/auth/register', { email, password, confirmPassword, consents });

export const verifyEmail = (token) =>
  api.get('/auth/verify-email', { params: { token } });

export const resendVerificationEmail = (email) =>
  api.post('/auth/resend-verification', { email });

// Sign-out endpoint. The refresh token lives in an HttpOnly cookie, which is
// sent automatically because `api` is configured with withCredentials: true —
// the server reads it from req.cookies and revokes it. We don't need to pass
// anything in the body.
export const logout = () =>
  api.post('/auth/logout');

// Sign out on every device tied to this account (revokes all refresh tokens).
export const logoutAll = () =>
  api.post('/auth/logout-all');

// Password reset flow. The forgot-password endpoint always returns 200 with
// a generic message regardless of whether the email is registered, so the
// callers never need to inspect the response body.
export const forgotPassword = (email) =>
  api.post('/auth/forgot-password', { email });

export const resetPassword = (token, password) =>
  api.post('/auth/reset-password', { token, password });

// 2FA setup — returns { qrCode } (data URL). Caller renders the QR and prompts
// for a 6-digit code, then calls verifyTwoFactor() to enable 2FA on the account.
export const setupTwoFactor = () =>
  api.post('/auth/2fa/setup');

export const verifyTwoFactor = (token) =>
  api.post('/auth/2fa/verify', { token });

// ─── Child intro API ──────────────────────────────────────────────────────────

export const getIntroStatus = () =>
  api.get('/child/intro-status');

export const markIntroComplete = () =>
  api.post('/child/intro-complete');

// ─── Child session API ────────────────────────────────────────────────────────

export const getSessionConfig = () =>
  api.get('/child/session-config');

export const startSession = (subject, mode) =>
  api.post('/child/session/start', { subject, mode });

export const endSession = (sessionId) =>
  api.post(`/child/session/${sessionId}/end`);

export const getSessions = () =>
  api.get('/child/sessions');

// Finished sessions only, newest first, filtered server-side.
//
// getSessions() returns a short window of sessions in ANY state and exists
// for SubjectSelector's resume prompts. The history view must NOT use it:
// filtering its result down to sealed sessions in the browser means
// filtering after a limit, which is what made older chats unreachable.
export const getSessionHistory = (limit) =>
  api.get('/child/sessions/history', limit ? { params: { limit } } : undefined);

export const sendChatMessage = (sessionId, message, inputMethod = 'text') =>
  api.post('/child/chat', { sessionId, message, inputMethod });

export const getSessionMessages = (sessionId) =>
  api.get(`/child/session/${sessionId}/messages`);

export const reportMessage = (sessionId, messageId, reason) =>
  api.post('/child/report', { sessionId, messageId, ...(reason ? { reason } : {}) });

export const synthesizeTts = (text, voiceName) =>
  api.post('/child/tts', { text, ...(voiceName ? { voiceName } : {}) });

// ─── Parent children API ──────────────────────────────────────────────────────

export const getChildren = () =>
  api.get('/parent/children');

// The learning-adaptations catalogue, served rather than hardcoded here so the
// checkboxes a parent sees and the enum the API validates against cannot drift.
export const getAdaptations = () =>
  api.get('/parent/adaptations');

export const updateChild = (childId, fields) =>
  api.put(`/parent/children/${childId}`, fields);

export const updateChildPin = (childId, pin) =>
  api.put(`/parent/children/${childId}/pin`, { pin });

export const deleteChild = (childId) =>
  api.delete(`/parent/children/${childId}`);

// ─── Parent dashboard API ─────────────────────────────────────────────────────

export const getDashboard = () =>
  api.get('/parent/dashboard');

export const getChildSessions = (childId) =>
  api.get(`/parent/children/${childId}/sessions`);

export const getParentSessionDetail = (sessionId) =>
  api.get(`/parent/sessions/${sessionId}`);

export const getSafeguardingAlerts = () =>
  api.get('/parent/safeguarding');

// Session-scoped flags with review status — used by SessionDetail to
// highlight the specific message that triggered each flag.
export const getSessionFlags = (sessionId) =>
  api.get(`/parent/sessions/${sessionId}/flags`);

// Records a parent review against a flag. reviewType is one of
// 'no_action' | 'action_taken' | 'contacted_support'. actionTaken is only
// persisted server-side when reviewType === 'action_taken'.
export const reviewSafeguardingFlag = (flagId, { reviewType, actionTaken } = {}) =>
  api.post(`/parent/safeguarding/${flagId}/review`, { reviewType, actionTaken });

// ─── Parent onboarding API ────────────────────────────────────────────────────

export const getOnboardingStatus = () =>
  api.get('/parent/onboarding/status');

export const completeOnboarding = () =>
  api.post('/parent/onboarding/complete');

export const setDigestPreference = (enabled) =>
  api.put('/parent/digest-preference', { enabled });

// Consent records (DPIA / ICO Children's Code retention). The server returns
// the three required consent rows captured at registration plus any
// withdrawals; admins display them under the parent's "Your data" controls.
export const getConsentRecords = () =>
  api.get('/parent/consent-records');

// ─── Weekly summaries API ─────────────────────────────────────────────────────

export const getWeeklySummaries = (childId) =>
  api.get(`/parent/children/${childId}/weekly-summaries`);

// ─── Learning context API ─────────────────────────────────────────────────────

export const getLearningContext = (childId) =>
  api.get(`/parent/children/${childId}/learning-context`);

export const clearLearningContext = (childId, subject) =>
  api.delete(`/parent/children/${childId}/learning-context/${encodeURIComponent(subject)}`);

export default api;
